import React from "react";
import { COLORS } from "../utils/constants";
import { GeoData } from "../services/locationService";
import { translations, LanguageCode } from "../utils/translations";

interface PricingProps {
  geo: GeoData | null;
  lang?: LanguageCode;
}

const Pricing: React.FC<PricingProps> = ({ geo, lang = "en" }) => {
  const content = translations[lang].pricingPage;

  return (
    <section
      style={{
        padding: "20px 6% 24px",
        background: "linear-gradient(to bottom, #ffffff, #f8f9ff)",
      }}
    >
      {/* HERO */}
      <div
        style={{
          textAlign: "center",
          maxWidth: "900px",
          margin: "0 auto 16px",
        }}
      >
        <h1
          style={{
            fontSize: "3rem",
            fontWeight: 900,
            color: COLORS.brandPurple,
            marginBottom: "15px",
          }}
        >
          {content.heroTitle}
        </h1>

        <h2
          style={{
            fontSize: "1.5rem",
            fontWeight: 600,
            color: COLORS.brandTeal,
            marginBottom: "20px",
          }}
        >
          {content.heroSubtitle}
        </h2>

        <p
          style={{
            fontSize: "1rem",
            color: "#4A5568",
          }}
        >
          {content.heroDesc}
        </p>
      </div>

      {/* MAIN TIERS */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
          gap: "30px",
          maxWidth: "1200px",
          margin: "0 auto",
        }}
      >
        {content.plans.map((plan, index) => (
          <PricingCard
            key={plan.title}
            title={plan.title}
            price={plan.price}
            highlight={index === 1}
            features={plan.features}
            mostPopular={content.mostPopular}
            getStarted={content.getStarted}
          />
        ))}
      </div>

      {/* ADD-ONS SECTION */}
      <div
        style={{
          marginTop: "32px",
          maxWidth: "1100px",
          marginLeft: "auto",
          marginRight: "auto",
        }}
      >
        <h2
          style={{
            textAlign: "center",
            fontSize: "2rem",
            fontWeight: 800,
            color: COLORS.brandPurple,
            marginBottom: "24px",
          }}
        >
          {content.addOnsTitle}
        </h2>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
            gap: "20px",
          }}
        >
          {content.addOns.map((item, index) => (
            <div
              key={index}
              style={{
                background: "#ffffff",
                padding: "20px",
                borderRadius: "14px",
                boxShadow: "0 10px 25px rgba(0,0,0,0.05)",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                fontWeight: 600,
              }}
            >
              <span>{item.name}</span>
              <span style={{ color: COLORS.brandTeal }}>{item.price}</span>
            </div>
          ))}
        </div>
      </div>

      {/* TRUST SECTION */}
      <div
        style={{
          textAlign: "center",
          marginTop: "24px",
          color: "#718096",
          fontSize: "0.95rem",
        }}
      >
        {content.trust}
      </div>
    </section>
  );
};

export default Pricing;

/* ---------------------------------- */
/* CARD COMPONENT */
/* ---------------------------------- */

interface PricingCardProps {
  title: string;
  price: string;
  features: string[];
  highlight: boolean;
  mostPopular: string;
  getStarted: string;
}

const PricingCard: React.FC<PricingCardProps> = ({
  title,
  price,
  features,
  highlight,
  mostPopular,
  getStarted,
}) => {
  return (
    <div
      style={{
        background: "#ffffff",
        padding: "16px",
        borderRadius: "20px",
        boxShadow: highlight
          ? `0 20px 40px rgba(113,75,103,0.15)`
          : "0 10px 30px rgba(0,0,0,0.06)",
        border: highlight ? `2px solid ${"#714B67"}` : "1px solid #f0f0f0",
        transform: highlight ? "scale(1.03)" : "scale(1)",
        transition: "0.3s ease",
      }}
    >
      {highlight && (
        <div
          style={{
            background: "#714B67",
            color: "white",
            padding: "6px 12px",
            borderRadius: "20px",
            fontSize: "0.75rem",
            display: "inline-block",
            marginBottom: "15px",
          }}
        >
          {mostPopular}
        </div>
      )}

      <h3
        style={{
          fontSize: "1.5rem",
          fontWeight: 800,
          marginBottom: "10px",
          color: "#714B67",
        }}
      >
        {title}
      </h3>

      <div
        style={{
          fontSize: "1.2rem",
          fontWeight: 700,
          marginBottom: "25px",
          color: "#017E84",
        }}
      >
        {price}
      </div>

      <ul
        style={{
          listStyle: "none",
          padding: 0,
          marginBottom: "16px",
          lineHeight: 1.8,
          color: "#4A5568",
        }}
      >
        {features.map((feature, index) => (
          <li key={index}>✔ {feature}</li>
        ))}
      </ul>

      <button
        style={{
          width: "100%",
          padding: "12px",
          borderRadius: "10px",
          border: "none",
          fontWeight: 700,
          cursor: "pointer",
          background: highlight ? "#714B67" : "#017E84",
          color: "white",
        }}
      >
        {getStarted}
      </button>
    </div>
  );
};
